import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useMenu } from "@/features/menu/store";
import { getCurrentWeekStart } from "@/features/menu/themePreference";
import type { MenuPlan, ValidatedMenu } from "@/types/menu";

/**
 * Query key for the latest server-validated menu. Exported so the validate
 * mutation can prime this cache after persisting, keeping the TanStack Query
 * cache and the Zustand store in agreement (see the single-writer note below).
 */
export const MENU_LATEST_KEY = ["menu", "latest"] as const;

/** Stable fingerprint of a plan's meals (day + slot → recipe slug). Slot
 * order is normalised because the server round-trips plans through jsonb. */
function mealsFingerprint(plan: MenuPlan): string {
  return plan.days
    .map((d) => {
      const slots = Object.entries(d.meals)
        .map(([slot, recipe]) => `${slot}=${recipe?.slug ?? ""}`)
        .sort()
        .join(",");
      return `${d.day}:${slots}`;
    })
    .join("|");
}

/**
 * Loads the active MenuPlan. Priority:
 *   1. Zustand store (persisted to Capacitor Preferences) — instant on same device
 *   2. Supabase `/api/v1/menu/latest` — cross-device sync of validated plans
 *   3. Fallback `/api/v1/menu/preview` — hardcoded demo data for first-run UX
 *
 * The API fetches are deferred until the Zustand persisted store has
 * finished hydrating (`_hydrated`). This prevents a race where the API
 * returns a plan that gets overwritten by the stale persisted plan a
 * moment later, which could cause the user to briefly see one week's
 * menu and then another.
 *
 * Single-writer invariant: the `["menu","latest"]` query cache is the ONLY
 * source the adoption effect below copies into Zustand. Anything that mutates
 * the persisted/validated menu (the validate mutation) must also prime this
 * cache via `setQueryData(MENU_LATEST_KEY, ...)` so the server cache and the
 * store never disagree — otherwise the adoption effect, seeing a stale
 * `serverMenuId !== menuId`, would overwrite the freshly-validated plan.
 */
export function useMenuPlan(): {
  plan: MenuPlan | null;
  /** True when the displayed plan is the hardcoded demo (not user-generated). */
  isPreview: boolean;
  /** True when the plan is the user's own (not the demo) and its week_start
   * is the current Monday (Europe/Paris). False for last week's plan. */
  isCurrentWeek: boolean;
  /** True once `/menu/latest` has answered (success or error). */
  serverResolved: boolean;
  /** True when the server holds a validated menu for the current week —
   * possibly not yet copied into the store (validated on another device). */
  hasCurrentWeekServerMenu: boolean;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
} {
  const plan = useMenu((s) => s.plan);
  const menuId = useMenu((s) => s.menuId);
  const hydrated = useMenu((s) => s._hydrated);
  const setPlan = useMenu((s) => s.setPlan);
  const setMenuId = useMenu((s) => s.setMenuId);

  // Priority 2: latest validated menu from Supabase — always fetch once
  // hydrated so that a menu validated on another device is picked up.
  const latestQuery = useQuery({
    queryKey: MENU_LATEST_KEY,
    queryFn: () => api<ValidatedMenu>("/api/v1/menu/latest"),
    enabled: hydrated,
    retry: false,
    staleTime: 60_000, // 1 minute — short enough for refetchOnWindowFocus to sync cross-device changes
  });

  // Never present a past week's menu as the current one (audit M16): the
  // backend serves only this week's menu on /menu/latest, but a stale cache
  // entry (e.g. primed last Sunday) or an older backend could still hand us
  // last week's. Treat it as "no server menu".
  const serverMenu =
    latestQuery.data &&
    String(latestQuery.data.plan.week_start ?? "") >= getCurrentWeekStart()
      ? latestQuery.data
      : null;
  const noServerMenu =
    latestQuery.isError || (latestQuery.isSuccess && serverMenu === null);

  // Priority 3: hardcoded preview demo (first run / no validated menu)
  const previewQuery = useQuery({
    queryKey: ["menu", "preview"],
    queryFn: () => api<MenuPlan>("/api/v1/menu/preview"),
    enabled: hydrated && !plan && noServerMenu,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!serverMenu) return;
    const serverMenuId = serverMenu.menu_id;
    // Adopt the server plan when:
    // 1. No local plan (first run / signed in on a new device).
    // 2. Local plan is already validated (menuId is set) but the server has a
    //    different UUID — the user validated a new menu on another device.
    // Don't overwrite an unvalidated draft (plan exists but menuId is null).
    if (
      !plan ||
      (menuId !== null && serverMenuId !== null && serverMenuId !== menuId)
    ) {
      setPlan(serverMenu.plan);
      setMenuId(serverMenuId);
      return;
    }
    // 3. Local plan is validated but lost its menuId (the ↻ button clears it
    //    before starting a regeneration that may then fail, e.g. 402). Re-link
    //    it to the server menu of the same week so sharing keeps working.
    //    Drafts (status !== "validated") are never linked, and neither is a
    //    plan whose meals differ from the server copy (e.g. a local swap that
    //    was never persisted): sharing it would share a different menu.
    if (
      menuId === null &&
      serverMenuId !== null &&
      plan.status === "validated" &&
      String(plan.week_start) === String(serverMenu.plan.week_start) &&
      mealsFingerprint(plan) === mealsFingerprint(serverMenu.plan)
    ) {
      setMenuId(serverMenuId);
    }
  }, [serverMenu, plan, menuId, setPlan, setMenuId]);

  // Preview data is display-only: do NOT store in Zustand so that MenuPage
  // never offers to persist demo content.

  const resolvedPlan = plan ?? serverMenu?.plan ?? previewQuery.data ?? null;
  const isLoading =
    !resolvedPlan &&
    (latestQuery.isLoading || (noServerMenu && previewQuery.isLoading));
  const isError = !resolvedPlan && noServerMenu && previewQuery.isError;
  const error = previewQuery.error ?? latestQuery.error;
  const isPreview =
    resolvedPlan !== null && resolvedPlan === previewQuery.data && !plan;

  const isCurrentWeek =
    resolvedPlan !== null &&
    !isPreview &&
    String(resolvedPlan.week_start ?? "") === getCurrentWeekStart();

  const serverResolved = latestQuery.isSuccess || latestQuery.isError;
  const hasCurrentWeekServerMenu =
    serverMenu !== null &&
    String(serverMenu.plan.week_start ?? "") === getCurrentWeekStart();

  return {
    plan: resolvedPlan,
    isPreview,
    isCurrentWeek,
    serverResolved,
    hasCurrentWeekServerMenu,
    isLoading,
    isError,
    error,
    refetch: () => {
      latestQuery.refetch();
      previewQuery.refetch();
    },
  };
}
